import { BigNumber } from "bignumber.js";
import {
  Asset,
  Balance,
  CompletionRecord,
  SuccessRecord,
  CompletionState,
  Breakpoints,
  Transaction,
  AssetState,
  TransferType,
  TokenType,
  TransactingComponent,
} from "../types/general";

import { tokenDecimalToMantissa, tokenMantissaToDecimal } from "./scaling";
import { NetworkType } from "@airgap/beacon-sdk";
import { MichelCodecPacker, TezosToolkit } from "@taquito/taquito";
import { normalizeTransactionFailure } from "./failures";
import { TRANSACTION_DEADLINE_MS } from "./transactionSafety";

/**
 * Dummy signer that provides only publicKeyHash and publicKey for fee estimation.
 * No signing ever occurs — sign() throws immediately.
 */
class EstimationSigner {
  constructor(private pkh: string) {}
  async publicKeyHash() {
    return this.pkh;
  }
  async publicKey() {
    return "edpkvGfYw3LyB1UcCahKQk4rF2tvbMUk8GFiTuMjL75uGXrpvKXhjn";
  }
  async sign(): Promise<{
    bytes: string;
    sig: string;
    prefixSig: string;
    sbytes: string;
  }> {
    throw new Error("EstimationSigner: signing not supported");
  }
  async secretKey(): Promise<string | undefined> {
    return undefined;
  }
}

/**
 * Build a throw-away TezosToolkit configured for fee estimation via node RPC.
 * Uses EstimationSigner so no wallet extension is needed — the node simulates
 * the operation and returns gas/storage/fee estimates.
 */
export function makeEstimationToolkit(
  toolkit: TezosToolkit,
  userAddress: string
): TezosToolkit {
  // Reuse the same RPC client so estimates inherit endpoint failover behavior.
  const est = new TezosToolkit(toolkit.rpc);
  est.setPackerProvider(new MichelCodecPacker());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  est.setProvider({ signer: new EstimationSigner(userAddress) as any });
  return est;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<F extends (...args: any[]) => any>(
  fn: F,
  ms: number
): F {
  let timer: NodeJS.Timeout | null;
  return function (
    this: ThisParameterType<F>,
    ...args: Parameters<F>
  ): ReturnType<F> | void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, args);
    }, ms);
  } as F;
}

export const adjustBreakpointsForDpr = (
  breakpoints: Breakpoints
): Breakpoints => {
  const dpr: number = window.devicePixelRatio || 1;
  const adjustedBreakpoints = { ...breakpoints };

  for (const [key, value] of Object.entries(breakpoints.breakpoints.values)) {
    const breakpointKey = key as keyof typeof breakpoints.breakpoints.values;

    adjustedBreakpoints.breakpoints.values[breakpointKey] = value / dpr;

    //  adjustedBreakpoints.breakpoints.values[breakpointKey] = value;
  }
  return adjustedBreakpoints;
};
export const shorten = (first: number, last: number, str: string) => {
  return str.substring(0, first) + "..." + str.substring(str.length - last);
};

export function tezToMutez(amount: BigNumber) {
  return amount.multipliedBy(1000000);
}
export function mutezToTez(amount: BigNumber) {
  return amount.dividedBy(1000000);
}

export const balanceGreaterOrEqualTo = (
  balance1: Balance,
  balance2: Balance
): boolean => {
  return balance1.mantissa.isGreaterThanOrEqualTo(balance2.mantissa);
};
export const balanceBuilder = (
  value: BigNumber | number | string,
  asset: Asset,
  isMantissa?: boolean
): Balance => {
  const decimal: BigNumber = isMantissa
    ? tokenMantissaToDecimal(value, asset)
    : new BigNumber(value);
  const mantissa: BigNumber = isMantissa
    ? new BigNumber(value)
    : tokenDecimalToMantissa(value, asset);
  const geq = (balance: Balance): boolean => {
    return mantissa.isGreaterThanOrEqualTo(balance.mantissa);
  };
  return {
    decimal,
    mantissa,
    string: decimal.toFixed(),
    greaterOrEqualTo: geq,
  };
};

export function completionRecordSuccess(
  success: SuccessRecord
): CompletionRecord {
  return [CompletionState.SUCCESS, success] as CompletionRecord;
}

export function completionRecordFailed(
  error: unknown,
  component?: TransactingComponent,
  network?: NetworkType
): CompletionRecord {
  return [
    CompletionState.FAILED,
    normalizeTransactionFailure(error, component, network),
  ] as CompletionRecord;
}

export function showAlert(
  record: CompletionRecord | undefined
): CompletionRecord | undefined {
  return record;
}

export function transactionToAssetStates(
  transaction: Transaction
): AssetState[] {
  const assetStates: AssetState[] = [];

  // Handle send assets
  transaction.sendAsset.forEach((asset, index) => {
    assetStates.push({
      transferType: TransferType.SEND,
      amount: transaction.sendAmount[index],
      balance: transaction.sendAssetBalance[index],
      asset: asset,
    });
  });

  // Handle receive assets
  transaction.receiveAsset.forEach((asset, index) => {
    assetStates.push({
      transferType: TransferType.RECEIVE,
      amount: transaction.receiveAmount[index],
      balance: transaction.receiveAssetBalance[index],
      asset: asset,
    });
  });

  return assetStates;
}

export function getAssetStateByTransactionTypeAndAsset(
  transferType: TransferType,
  asset: Asset,
  assetStates: AssetState[] | undefined
): AssetState | undefined {
  if (assetStates)
    return assetStates.find(
      (state) =>
        state.transferType === transferType && state.asset.name === asset.name
    );
  return undefined;
}

export function isNumeric(n: string): boolean {
  return !isNaN(parseFloat(n)) && isFinite(parseFloat(n));
}

export function toNumber(n: string): number {
  if (isNumeric(n)) {
    return parseFloat(n);
  } else throw new Error("Not a number");
}

export function cleanNumericString(n: string): string {
  return n.replace(/[^0-9.]+/g, "").replace(/(\..*)\./g, "$1");
}

export function formatWithSubscript(
  value: BigNumber,
  decimals: number
): string {
  value = value.abs();
  if (value.gte(0.01) || value.isZero()) {
    return value.isInteger() ? value.toString() : value.toFixed(decimals);
  }
  const nOfDecimals = value.decimalPlaces() ?? 0;
  const significantDigits = value.sd();
  const nOfZeroes = nOfDecimals - significantDigits;

  const significantPart = value
    .shiftedBy(nOfDecimals)
    .precision(decimals)
    .toString()
    .replace(/0+$/, "");

  const subscriptNumberOfZeroes = nOfZeroes
    .toString()
    .split("")
    .map((digit) => String.fromCodePoint(Number("0x208" + digit)))
    .join("");

  return `0.0${subscriptNumberOfZeroes}${significantPart}`;
}

export function getTxDeadline(): Date {
  return new Date(Date.now() + TRANSACTION_DEADLINE_MS);
}

export type SupportedNetwork =
  | NetworkType.MAINNET
  | NetworkType.SHADOWNET
  | NetworkType.CUSTOM;

export const EXPLORERS: Record<SupportedNetwork, string> = {
  [NetworkType.MAINNET]: "https://tzkt.io/",
  [NetworkType.SHADOWNET]: "https://shadownet.tzkt.io/",
  [NetworkType.CUSTOM]: "https://previewnet.tezosx.tzkt.io/",
};

export function getExplorer(network: NetworkType): string {
  if (network in EXPLORERS) {
    return EXPLORERS[network as SupportedNetwork];
  }
  throw new Error(`Unsupported network: ${network}`);
}

export const getTzktApiUrl = (networkType: NetworkType): string => {
  switch (networkType) {
    case NetworkType.MAINNET:
      return "https://api.tzkt.io";
    case NetworkType.SHADOWNET:
      return "https://api.shadownet.tzkt.io";
    case NetworkType.CUSTOM:
      return "https://api.previewnet.tezosx.tzkt.io";
    default:
      return "https://api.tzkt.io";
  }
};

const assertTzktResponse = (response: Response): void => {
  if (!response.ok) {
    throw new Error(`TzKT balance request failed with HTTP ${response.status}`);
  }
};

const parseExactBalance = (value: unknown, field: string): BigNumber => {
  if (typeof value !== "string" || !/^\d+$/.test(value)) {
    throw new Error(`TzKT returned an invalid ${field}`);
  }
  return new BigNumber(value);
};

const asTzktRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined;

export async function getBalanceFromTzKT(
  network: NetworkType,
  address: string,
  asset: Asset
): Promise<BigNumber> {
  const api = getTzktApiUrl(network);
  switch (asset.type) {
    case TokenType.XTZ: {
      const response = await fetch(
        `${api}/v1/accounts/${encodeURIComponent(address)}/balance`
      );
      assertTzktResponse(response);
      return parseExactBalance((await response.text()).trim(), "XTZ balance");
    }
    case TokenType.FA12:
    case TokenType.FA2: {
      const tokenId = asset.tokenId ?? 0;
      if (!Number.isSafeInteger(tokenId) || tokenId < 0) {
        throw new Error("Configured token ID must be a non-negative integer");
      }

      const query = new URLSearchParams({
        account: address,
        "token.contract": asset.address,
        "token.tokenId": tokenId.toString(),
        limit: "2",
      });
      const response = await fetch(`${api}/v1/tokens/balances?${query}`);
      assertTzktResponse(response);

      const data: unknown = await response.json();
      if (!Array.isArray(data)) {
        throw new Error("TzKT returned a malformed token balance response");
      }
      if (data.length === 0) return new BigNumber(0);
      if (data.length !== 1) {
        throw new Error("TzKT returned an ambiguous token balance response");
      }

      const balance = asTzktRecord(data[0]);
      const account = asTzktRecord(balance?.account);
      const token = asTzktRecord(balance?.token);
      const contract = asTzktRecord(token?.contract);

      if (
        account?.address !== address ||
        contract?.address !== asset.address ||
        token?.tokenId !== tokenId.toString()
      ) {
        throw new Error(
          "TzKT token balance identity did not match the request"
        );
      }

      return parseExactBalance(balance?.balance, "token balance");
    }
  }
}
